// src/store/messaging/messagingSlice.js
import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  // État du dock
  dock: {
    isOpen: false,
    isMinimized: false,
  },

  // Conversations récentes (pour le dock)
  recentConversations: [],

  // Conversations ouvertes (fenêtres flottantes)
  openConversations: new Map(),

  // Compteurs
  totalUnread: 0,

  // États en temps réel
  typingUsers: new Map(), // conversationId_userId -> { userId, conversationId, timestamp }
  onlineUsers: new Set(),

  // États de chargement
  loading: {
    conversations: false,
    messages: false,
    sending: false,
  },

  // Erreurs
  error: null,

  // Cache
  messagesCache: new Map(), // conversationId -> messages[]
  usersCache: new Map(), // userId -> user object
}

const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  reducers: {
    // Actions pour le dock
    setDockOpen: (state, action) => {
      state.dock.isOpen = action.payload
    },

    setDockMinimized: (state, action) => {
      state.dock.isMinimized = action.payload
    },

    // Actions pour les conversations récentes
    setRecentConversations: (state, action) => {
      state.recentConversations = action.payload
    },

    addRecentConversation: (state, action) => {
      const conversation = action.payload
      const existingIndex = state.recentConversations.findIndex((c) => c.id === conversation.id)

      if (existingIndex >= 0) {
        // Mettre à jour et déplacer en première position
        state.recentConversations.splice(existingIndex, 1)
      }

      state.recentConversations.unshift(conversation)

      // Limiter à 10 conversations récentes
      if (state.recentConversations.length > 10) {
        state.recentConversations = state.recentConversations.slice(0, 10)
      }
    },

    updateRecentConversation: (state, action) => {
      const { conversationId, updates } = action.payload
      const index = state.recentConversations.findIndex((c) => c.id === conversationId)

      if (index >= 0) {
        state.recentConversations[index] = {
          ...state.recentConversations[index],
          ...updates,
        }
      }
    },

    // Actions pour les conversations ouvertes
    openConversation: (state, action) => {
      const conversation = action.payload
      const openConversationsMap = new Map(state.openConversations)

      openConversationsMap.set(conversation.id, {
        ...conversation,
        isMinimized: false,
        lastActivity: Date.now(),
      })

      state.openConversations = openConversationsMap
    },

    closeConversation: (state, action) => {
      const conversationId = action.payload
      const openConversationsMap = new Map(state.openConversations)
      openConversationsMap.delete(conversationId)
      state.openConversations = openConversationsMap
    },

    minimizeConversation: (state, action) => {
      const conversationId = action.payload
      const openConversationsMap = new Map(state.openConversations)
      const conversation = openConversationsMap.get(conversationId)

      if (conversation) {
        openConversationsMap.set(conversationId, {
          ...conversation,
          isMinimized: !conversation.isMinimized,
          lastActivity: Date.now(),
        })
        state.openConversations = openConversationsMap
      }
    },

    updateConversationMessages: (state, action) => {
      const { conversationId, messages } = action.payload
      const openConversationsMap = new Map(state.openConversations)
      const conversation = openConversationsMap.get(conversationId)

      if (conversation) {
        openConversationsMap.set(conversationId, {
          ...conversation,
          messages: messages,
          lastActivity: Date.now(),
        })
        state.openConversations = openConversationsMap
      }

      // Mettre à jour le cache
      const cacheMap = new Map(state.messagesCache)
      cacheMap.set(conversationId, messages)
      state.messagesCache = cacheMap
    },

    addMessageToConversation: (state, action) => {
      const { conversationId, message } = action.payload
      const openConversationsMap = new Map(state.openConversations)
      const conversation = openConversationsMap.get(conversationId)

      if (conversation) {
        const updatedMessages = [...(conversation.messages || []), message]
        openConversationsMap.set(conversationId, {
          ...conversation,
          messages: updatedMessages,
          lastActivity: Date.now(),
        })
        state.openConversations = openConversationsMap
      }

      // Mettre à jour le cache
      const cacheMap = new Map(state.messagesCache)
      const cachedMessages = cacheMap.get(conversationId) || []
      cacheMap.set(conversationId, [...cachedMessages, message])
      state.messagesCache = cacheMap

      // Mettre à jour les conversations récentes
      const recentIndex = state.recentConversations.findIndex((c) => c.id === conversationId)
      if (recentIndex >= 0) {
        const updatedConversation = {
          ...state.recentConversations[recentIndex],
          messages: [message],
          updatedAt: message.created_at || new Date().toISOString(),
        }

        // Si le message n'est pas de l'utilisateur actuel, incrémenter unreadCount
        if (message.sender_id !== state.currentUserId) {
          updatedConversation.unreadCount = (updatedConversation.unreadCount || 0) + 1
          state.totalUnread += 1
        }

        // Déplacer en première position
        state.recentConversations.splice(recentIndex, 1)
        state.recentConversations.unshift(updatedConversation)
      }
    },

    // Actions pour les compteurs
    setTotalUnread: (state, action) => {
      state.totalUnread = action.payload
    },

    incrementUnread: (state) => {
      state.totalUnread += 1
    },

    decrementUnread: (state, action) => {
      const count = action.payload || 1
      state.totalUnread = Math.max(0, state.totalUnread - count)
    },

    markConversationAsRead: (state, action) => {
      const conversationId = action.payload

      // Mettre à jour les conversations récentes
      const recentIndex = state.recentConversations.findIndex((c) => c.id === conversationId)
      if (recentIndex >= 0) {
        const unreadCount = state.recentConversations[recentIndex].unreadCount || 0
        state.recentConversations[recentIndex].unreadCount = 0
        state.totalUnread = Math.max(0, state.totalUnread - unreadCount)
      }
    },

    // Actions pour les états en temps réel
    setTypingUsers: (state, action) => {
      state.typingUsers = new Map(action.payload)
    },

    addTypingUser: (state, action) => {
      const { userId, conversationId } = action.payload
      const typingMap = new Map(state.typingUsers)
      const key = `${conversationId}_${userId}`

      typingMap.set(key, {
        userId,
        conversationId,
        timestamp: Date.now(),
      })

      state.typingUsers = typingMap
    },

    removeTypingUser: (state, action) => {
      const { userId, conversationId } = action.payload
      const typingMap = new Map(state.typingUsers)
      const key = `${conversationId}_${userId}`

      typingMap.delete(key)
      state.typingUsers = typingMap
    },

    setOnlineUsers: (state, action) => {
      state.onlineUsers = new Set(action.payload)
    },

    addOnlineUser: (state, action) => {
      const userId = action.payload
      const onlineSet = new Set(state.onlineUsers)
      onlineSet.add(userId)
      state.onlineUsers = onlineSet
    },

    removeOnlineUser: (state, action) => {
      const userId = action.payload
      const onlineSet = new Set(state.onlineUsers)
      onlineSet.delete(userId)
      state.onlineUsers = onlineSet
    },

    // Actions pour les états de chargement
    setLoading: (state, action) => {
      const { type, value } = action.payload
      state.loading[type] = value
    },

    // Actions pour les erreurs
    setError: (state, action) => {
      state.error = action.payload
    },

    clearError: (state) => {
      state.error = null
    },

    // Actions pour le cache
    cacheUser: (state, action) => {
      const user = action.payload
      const cacheMap = new Map(state.usersCache)
      cacheMap.set(user.id, user)
      state.usersCache = cacheMap
    },

    // Reset complet
    resetMessaging: (state) => {
      return {
        ...initialState,
        usersCache: state.usersCache, // Conserver le cache des utilisateurs
      }
    },
  },
})

export const {
  setDockOpen,
  setDockMinimized,
  setRecentConversations,
  addRecentConversation,
  updateRecentConversation,
  openConversation,
  closeConversation,
  minimizeConversation,
  updateConversationMessages,
  addMessageToConversation,
  setTotalUnread,
  incrementUnread,
  decrementUnread,
  markConversationAsRead,
  setTypingUsers,
  addTypingUser,
  removeTypingUser,
  setOnlineUsers,
  addOnlineUser,
  removeOnlineUser,
  setLoading,
  setError,
  clearError,
  cacheUser,
  resetMessaging,
} = messagingSlice.actions

export default messagingSlice.reducer
